// src/components/SecurityIndicator.tsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';

const SecurityIndicator: React.FC = () => {
  const { user, logout } = useAuth();
  const [showTooltip, setShowTooltip] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(5 * 60 * 1000); // 5 minutes par défaut

  // Simple calcul du temps restant basé sur l'activité
  useEffect(() => {
    if (!user) return;

    // Récupérer la dernière activité du localStorage (sera gérée par ProtectedRoute)
    const getTimeRemaining = () => {
      const lastActivity = localStorage.getItem('lastActivity');
      if (lastActivity) {
        const elapsed = Date.now() - parseInt(lastActivity);
        const remaining = Math.max(0, (5 * 60 * 1000) - elapsed); // 5 minutes
        return remaining;
      }
      return 5 * 60 * 1000;
    };

    // Mettre à jour toutes les secondes
    const interval = setInterval(() => {
      setTimeRemaining(getTimeRemaining());
    }, 1000);

    // Initialiser
    setTimeRemaining(getTimeRemaining());

    return () => clearInterval(interval);
  }, [user]);

  if (!user) return null;

  const isWarning = timeRemaining < 120000; // Moins de 2 minutes
  const isCritical = timeRemaining < 60000; // Moins de 1 minute

  const getIndicatorColor = () => {
    if (isCritical) return 'bg-red-500';
    if (isWarning) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const getIndicatorIcon = () => {
    if (isCritical) return '🔴';
    if (isWarning) return '🟡';
    return '🟢';
  };

  const formatTimeRemaining = (ms: number) => {
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleSecureLogout = () => {
    console.info('🔒 Security Event: MANUAL_LOGOUT');
    logout();
  };

  return (
    <div className="relative">
      <div 
        className="flex items-center space-x-2 cursor-pointer"
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      >
        {/* Indicateur de statut */}
        <div className={`w-3 h-3 rounded-full ${getIndicatorColor()}`}></div>
        
        {/* Icône de sécurité */}
        <div className="text-sm">
          {getIndicatorIcon()}
        </div>
        
        {/* Temps restant (uniquement si warning) */}
        {isWarning && (
          <span className="text-xs text-red-600 font-mono">
            {formatTimeRemaining(timeRemaining)}
          </span>
        )}
      </div>

      {/* Tooltip */}
      {showTooltip && (
        <div className="absolute top-full mt-2 right-0 bg-gray-800 text-white text-xs rounded py-2 px-3 whitespace-nowrap z-20 shadow-lg">
          <div className="space-y-1">
            <div className="font-semibold">🔒 Sécurité de session</div>
            <div>Statut: Actif</div>
            <div>
              Temps restant: {formatTimeRemaining(timeRemaining)}
            </div>
            {isWarning && (
              <div className="text-yellow-300 font-semibold">
                ⚠️ Session expire bientôt
              </div>
            )}
            <div className="border-t border-gray-600 pt-1 mt-1">
              <button
                onClick={handleSecureLogout}
                className="text-red-300 hover:text-red-100 text-xs"
              >
                Déconnexion sécurisée
              </button>
            </div>
          </div>
          
          {/* Flèche du tooltip */}
          <div className="absolute bottom-full right-4 border-4 border-transparent border-b-gray-800"></div>
        </div>
      )}
    </div>
  );
};

export default SecurityIndicator;